import { useState, useRef, useEffect } from "react";
import { Pencil } from "lucide-react";

export interface KiiEditableTitleProps {
  value: string;
  onSave?: (newValue: string) => void;
  editable?: boolean;
  className?: string;
  inputClassName?: string;
  as?: "h1" | "h2" | "h3" | "span";
  testId?: string;
}

/**
 * KiiEditableTitle - Inline-editable title with optimistic save.
 *
 * Shows a title with a hover pencil icon. Click to edit.
 * Enter or blur saves and closes immediately. Escape cancels.
 * onSave is fire-and-forget (no async blocking).
 */
export function KiiEditableTitle({
  value,
  onSave,
  editable,
  className = "text-3xl sm:text-4xl font-bold",
  inputClassName,
  as: Tag = "h1",
  testId,
}: KiiEditableTitleProps) {
  const isEditable = editable ?? !!onSave;
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    if (!isEditable) return;
    setEditedValue(value);
    setIsEditing(true);
  };

  const handleSave = () => {
    const trimmed = editedValue.trim();
    if (trimmed && trimmed !== value && onSave) {
      onSave(trimmed);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      setEditedValue(value);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editedValue}
        onChange={(e) => setEditedValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className={
          inputClassName ??
          `${className} bg-transparent border-b-2 border-primary focus:outline-none w-full min-w-0 text-foreground`
        }
        data-testid={testId ? `input-edit-${testId}` : "input-edit-title"}
      />
    );
  }

  return (
    <div
      className={`flex items-center gap-2 ${isEditable ? "group cursor-pointer" : ""}`}
      onClick={handleStartEdit}
    >
      <Tag
        className={`${className} truncate text-foreground`}
        data-testid={testId || "text-title"}
      >
        {value}
      </Tag>
      {isEditable && (
        <span className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded transition-colors opacity-0 group-hover:opacity-100 shrink-0">
          <Pencil className="h-4 w-4" />
        </span>
      )}
    </div>
  );
}
